import type { InboxNotificationLinkTarget } from "./v2/inboxNotificationLink";
import { iriToId } from "@verbleif/lib/src/composables/Utils/iri.helpers";
import { parseInboxNotificationLink, parseWebNotificationLink, quietHoursSummaryTarget } from "./v2/inboxNotificationLink";
import { userNotificationIdFromIri } from "./v2/notifications.api";

export interface PushNotificationReference {
  /** A notifications v2 push (an item or a quiet-hours summary); a legacy push names a legacy notification. */
  isUserNotification: boolean
  /** The notification to mark read when the push is opened, if the push names one. */
  notificationId: number | null
  /** The screen the push opens. */
  target: InboxNotificationLinkTarget | null
}

function idOf(iri: unknown): number | null {
  if (typeof iri !== "string" || !iri) {
    return null;
  }
  try {
    return iriToId(iri);
  } catch {
    return null;
  }
}

/** The API sends v2 pushes a web URL; older payloads an app link. */
function linkTarget(link: string | undefined): InboxNotificationLinkTarget | null {
  return parseWebNotificationLink(link) ?? parseInboxNotificationLink(link);
}

/**
 * Reads the notification and the screen a web push refers to. A v2 push carries `{notification, location, link,
 * topic}` (the user notification IRI and the item's web URL, or an app link in older payloads), a quiet-hours summary push `{type: "quiet_hours_summary",
 * channel, windowEndsAt, count, link}` (no single item), a legacy push `{@id, type, task, report}`.
 */
export function readPushNotificationData(data: Record<string, string>): PushNotificationReference {
  if (data.type === "quiet_hours_summary") {
    return {
      isUserNotification: true,
      notificationId: null,
      target: quietHoursSummaryTarget(data.channel, data.windowEndsAt) ?? linkTarget(data.link),
    };
  }

  const userNotificationId = userNotificationIdFromIri(data.notification);
  if (userNotificationId !== null) {
    return {
      isUserNotification: true,
      notificationId: userNotificationId,
      target: linkTarget(data.link),
    };
  }

  const notificationId = idOf(data["@id"]);
  const taskId = data.type === "task" ? idOf(data.task) : null;
  const reportId = data.type === "report" ? idOf(data.report) : null;
  let target: InboxNotificationLinkTarget | null = null;
  if (taskId !== null) {
    target = { type: "task", id: taskId };
  } else if (reportId !== null) {
    target = { type: "report", id: reportId };
  }

  return { isUserNotification: false, notificationId, target };
}
